import {Component, OnInit} from '@angular/core';
import {BehaviorSubject} from 'rxjs/BehaviorSubject';
import {FormControl} from '@angular/forms';
import {Subject} from 'rxjs/Subject';
import {Material} from '../models/material';
import {animate, style, trigger, transition} from '@angular/animations';
import {CuiBaseComponent} from '@cicero/webui/lib/core/shared/base-component';
import {ActivatedRoute, Params, Router} from '@angular/router';
import {MaterialService} from '../material.service';
import {MatDialog} from '@angular/material';
import {Observable} from 'rxjs/Observable';
import {MaterialDialogComponent} from '../material-dialog/material-dialog.component';

const animationDuration = 200;

@Component({
  selector: 'app-material-list',
  templateUrl: './material-list.component.html',
  styleUrls: ['./material-list.component.scss'],
  animations: [
    trigger('flyOut', [
      transition('* => hidden', [
        style({transform: 'translateX(0)'}),
        animate(
          `${animationDuration / 2}ms ${animationDuration / 2}ms cubic-bezier(0.4, 0.0, 0.2, 1)`,
          style({opacity: 0, transform: 'translateX(100%)'}))
      ])
    ])
  ]
})
export class MaterialListComponent extends CuiBaseComponent implements OnInit {

  materials = new BehaviorSubject<Material[]>([]);
  visibleMaterials: Material[] = [];

  displayedMaterials = new BehaviorSubject<Material[]>([]);

  page = 0;
  numberOfResults = 0;
  numberOfPages = 0;
  numberOfHidden = 0;
  queryDescriptor = '';
  showQueryDescriptor = false;

  materialStateChange = new Subject();
  hideHandled = new FormControl(false);

  hasLoaded = false;
  error?: string;

  get isOffline() {
    return !window.navigator.onLine;
  }

  constructor(public route: ActivatedRoute,
              public router: Router,
              public materialService: MaterialService,
              public dialog: MatDialog) {
    super();
  }

  ngOnInit() {
    this.pushSubscription(
      this.setupMaterialFetching(),
      this.setupMaterialFiltering()
    );
  }

  setupMaterialFetching() {
    return this.route.queryParams
      .do(() => this.hasLoaded = false)
      .switchMap((queryParams: Params) => this.materialService.search(queryParams, 0))
      .subscribe(
        result => {
          this.materials.next(result.materials);
          this.queryDescriptor = result.query;
          this.numberOfResults = result.numberOfResults;
          this.numberOfPages = result.numberOfPages;
          this.showQueryDescriptor = true;
          this.hasLoaded = true;
          this.page = 0;

          this.addCovers(result.materials)
            .subscribe(materials => this.materials.next(materials));
        },
        () => this.error = 'Noget gik galt da vi hentede søgeresultaterne.'
      );
  }

  setupMaterialFiltering() {
    return Observable.merge(
      this.hideHandled.valueChanges,
      this.materialStateChange.filter(() => this.hideHandled.value).delay(animationDuration),
      this.materials
    )
      .map(() => this.materials.getValue())
      .map(materials => this.filterMaterials(materials))
      .subscribe(materials => this.displayedMaterials.next(materials));
  }

  filterMaterials(materials: Material[]): Material[] {
    if (!this.hideHandled.value) {
      this.numberOfHidden = 0;
      return materials;
    }

    const filteredMaterials = materials.filter(material => !material.localState);
    this.numberOfHidden = materials.length - filteredMaterials.length;
    return filteredMaterials;
  }

  loadNextPage() {
    if (this.page >= this.numberOfPages - 1) {
      return;
    }

    this.materialService.search(this.route.snapshot.queryParams, ++this.page)
      .subscribe(result => {
        this.materials.next(this.materials.value.concat(result.materials));

        this.addCovers(result.materials)
          .subscribe(materials => this.materials.next(this.materials.value));
      });
  }

  showDialog(material: Material): void {
    const dialog = this.dialog.open(MaterialDialogComponent, {
      data: material,
      backdropClass: 'no-overflow-no-padding-backdrop'
    });

    dialog.componentInstance.materialStateChange.subscribe(() => this.materialStateChange.next());
  }

  undo(material: Material) {
    this.materialService.undo(material).subscribe();
    this.materialStateChange.next();
  }

  markFound(material: Material) {
    this.materialService.markFound(material).subscribe();
    this.materialStateChange.next();
  }

  markLost(material: Material) {
    this.materialService.markLost(material).subscribe(null, () => this.materialStateChange.next());
    this.materialStateChange.next();
  }

  addCovers(materials: Material[]): Observable<Material[]> {
    const faustNumbers = materials.filter(m => m.faustNumber).map(m => m.faustNumber);

    if (faustNumbers.length === 0) {
      return Observable.empty();
    }

    return this.materialService.getCovers(faustNumbers)
      .map(covers => {
        materials.forEach(material => {
          const cover = covers[material.faustNumber];

          if (cover) {
            material.cover = cover.detail;
          }
        });

        return materials;
      });
  }
}



// WEBPACK FOOTER //
// ./src/app/material/material-list/material-list.component.ts